<script setup lang="ts">
import { computed, nextTick, useId, type Component } from 'vue'

import { formatNumber, useListError, useListTotal } from '@/shared/lib'
import { Button } from '@/shared/ui/Button'
import { Select } from '@/shared/ui/Select'

/**
 * A `Select` backed by a search request rather than by a fixed list — what an entity's own
 * `<Entity>Select.vue` renders, over `useEntitySelect`. A select over a catalogue fetched
 * whole (a few hundred countries) takes `Select` directly and needs none of this.
 *
 * Wire it from the composable: `remote-method` ← `onSearch`, `load-more` ← `loadMore`,
 * `options` ← the mapped options, `loading` ← `isLoading`. Without `load-more` the field is
 * whatever the first page holds, which is a dead end for a list too long to guess at.
 *
 * The six flags below (`filterable remote clearable reserve-keyword remote-show-suffix
 * block`) are set here rather than in each entity select, so every searchable field behaves
 * alike. A caller chooses `multiple`, `allow-create` and the loading state.
 *
 * **Never virtualized, and not for the reason it is tempting to assume.** A page here is
 * `SELECT_PAGE_LIMIT` = 200 options — 3.3× `Select`'s own `VIRTUALIZE_FROM` of 60 — and
 * `loadMore` appends another 200 per scroll with no ceiling, so this list is neither short
 * nor bounded. It is opted out because infinite scroll depends on `popup-scroll`, and
 * **`popup-scroll` is emitted by `ElSelect` only; `ElSelectV2`, the virtualized path, does
 * not emit it** (checked in element-plus's own `select/src/useSelect.mjs`).
 *
 * So flipping this flag does not trade memory for scrolling — it silently ends paging, and
 * the dropdown quietly stops at its first 200 rows. Making it virtual means driving paging
 * off `ElSelectV2`'s own scroll events instead, which is a real piece of work and not a
 * flag change.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null | (string | number)[]
    options: Array<{
      label: string
      value: string | number | null
      status?: boolean | null
      statusText?: string
      icon?: Component
    }>
    /** Called with the typed query — pass the composable's `onSearch`. */
    remoteMethod?: (query: string) => void
    loading?: boolean
    placeholder?: string
    disabled?: boolean
    multiple?: boolean
    /** Let the user enter a value the search did not offer (free-text matchers). */
    allowCreate?: boolean
    loadMore?: () => void
    /**
     * Offer "All" over what the dropdown holds.
     *
     * It cannot mean everything: the list endpoints cap a page, and this field holds the
     * pages fetched so far. The header says so rather than implying a whole-list selection
     * — and for a filter it matters less than it looks, since an empty field already means
     * every row.
     */
    selectAll?: boolean
  }>(),
  { placeholder: 'Type to search' }
)

/**
 * The dropdown asks for the next page as it nears its end.
 *
 * Element Plus reports the scroll offset alone (`popup-scroll` forwards the scrollbar's
 * event), so the element itself is looked up once per opening — through a class of this
 * field's own, since several dropdowns can be in the document at a time — and the
 * distance to the bottom is measured on it.
 */
const popperClass = `remote-select-${useId()}`

/**
 * Why the dropdown is empty, when it is empty because the request failed. Announced by
 * `useEntitySelect`, so a refused catalog reads as a refusal rather than as "No data".
 */
const listError = useListError()

/**
 * How many rows the list holds, announced by `useEntitySelect` — `null` for a field wired
 * up by hand, which then says only what it has.
 */
const listTotal = useListTotal()

/** How close to the end is close enough to ask, in pixels. */
const LOAD_MORE_THRESHOLD = 48

let scrollWrap: HTMLElement | null = null

function onVisibleChange(open: boolean) {
  scrollWrap = null
  if (!open) return

  nextTick(() => {
    scrollWrap = document.querySelector(
      `.${popperClass} .el-select-dropdown__wrap`
    )
  })
}

function onPopupScroll({ scrollTop }: { scrollTop: number }) {
  if (!scrollWrap || !props.loadMore) return

  // A list that does not scroll has no end to approach. Element Plus reports a scroll the
  // moment the dropdown opens, and on a short list every measurement below is zero — which
  // read as "the user has reached the bottom" and asked for a second page on open.
  if (scrollWrap.scrollHeight <= scrollWrap.clientHeight) return

  const left = scrollWrap.scrollHeight - scrollWrap.clientHeight - scrollTop

  if (left <= LOAD_MORE_THRESHOLD) props.loadMore()
}

/**
 * Element Plus hides the dropdown outright when a `remote` select has nothing typed
 * and no options (`dropdownMenuVisible = expanded && !isRemoteSearchEmpty`), so an
 * empty list reads as a broken control: the arrow flips and nothing opens. One
 * unpickable row keeps the panel on screen and says what happened. Not for an
 * allow-create field — there the typing itself is the answer.
 *
 * That row also has to carry the reason itself: `no-data-text` is rendered only when a
 * select has no options at all, and this one always has this. An empty catalogue and a
 * refused one would otherwise read exactly alike.
 *
 * The loading state is a row for the same reason, rather than element-plus's own `#loading`
 * slot, which lives inside that hidden menu — a field that fetches its list when the
 * dropdown opens would show nothing at all for as long as the request takes.
 */
const shownOptions = computed(() => {
  if (props.options.length || props.allowCreate) return props.options

  return [
    {
      label: props.loading
        ? 'Loading…'
        : (listError.value?.message ?? 'Nothing found'),
      value: '',
      disabled: true
    }
  ]
})

/**
 * What "all" actually covers here, said plainly on the header that offers it.
 *
 * Counted off `options` rather than `shownOptions`, which stands in a row of its own when
 * there is nothing to show — and against the list's real length when the list reports one,
 * so "the 200 loaded so far" says what it is 200 out of. That length is counted under
 * whatever is typed and whatever narrows the field, so it is the list the user is looking
 * at rather than the catalog behind it.
 *
 * A page that has reached the end drops the invitation to scroll: there is nothing further
 * down, and a field holding its whole list should not read as if it held a slice of one.
 */
const selectAllNote = computed(() => {
  if (!props.selectAll) return undefined

  const loaded = formatNumber(props.options.length)
  const total = listTotal.value
  const empty = 'leave the field empty to mean every one'

  if (total === null) {
    return `Covers the ${loaded} loaded so far — scroll for more, or ${empty}.`
  }

  return props.options.length < total
    ? `Covers the ${loaded} loaded so far out of ${formatNumber(total)} — scroll for more, or ${empty}.`
    : `Covers all ${formatNumber(total)} — or ${empty}.`
})

const emit = defineEmits<{
  (
    e: 'update:modelValue',
    value: string | number | null | (string | number)[]
  ): void
  (e: 'change', value: string | number | null | (string | number)[]): void
}>()
</script>

<template>
  <Select
    :model-value="modelValue ?? null"
    :options="shownOptions"
    :placeholder="placeholder"
    :loading="loading"
    :disabled="disabled"
    :multiple="multiple"
    :remote-method="remoteMethod"
    :allow-create="allowCreate"
    :default-first-option="allowCreate"
    :select-all="selectAll"
    :select-all-note="selectAllNote"
    :virtualized="false"
    :popper-class="popperClass"
    :no-data-text="listError?.message"
    block
    filterable
    remote
    clearable
    reserve-keyword
    remote-show-suffix
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
    @change="emit('change', $event)"
    @visible-change="onVisibleChange"
    @popup-scroll="onPopupScroll"
  >
    <template v-if="listError" #header>
      <Button link type="primary" size="small" @click="listError.retry()">
        Try again
      </Button>
    </template>
  </Select>
</template>
